// Every payment that actually moved money — and the only place to mark
// one disputed.
//
// WHY THIS EXISTS AT ALL
//
// There was no admin view of payments. Money moved, records were written,
// and the only way to see any of it was to read JSON on the server. The
// 71.98 that took money and granted nothing sat unnoticed for two days,
// and a second one for longer, because nothing showed them.
//
// WHY IT LISTS PENDING PURCHASES RATHER THAN payments.json
//
// A refund needs the provider's job AND payment references, and those
// live on the pending purchase. payments.json records what was GRANTED,
// not what the provider was asked to do. Anything that took money has a
// pending purchase — the direct grant route is refused once a provider is
// configured — so nothing refundable is missing from this view.
//
// It deliberately includes failed and still-pending records. "The buyer
// says they paid and got nothing" is exactly the case that needs looking
// at, and it is invisible in a list of successes.
import React, { useEffect, useState } from "react";
import { showToast } from "./toast.js";
import { usePaginatedList } from "./usePaginatedList.js";
import ListDisplayControls from "./ListDisplayControls.jsx";
import DataModeFilter from "./DataModeFilter.jsx";
import { siteDateTime } from "./siteTime.js";

function Frozen({ frozen, title, children }) {
  if (!frozen) return children;
  return (
    <fieldset disabled title={title} style={{ border: "none", padding: 0, margin: 0 }}>
      <div style={{ opacity: 0.55 }}>{children}</div>
    </fieldset>
  );
}

const STATUS_COLOURS = {
  confirmed: "var(--accent-green, #5ed46a)",
  failed: "var(--accent-red)",
  pending: "var(--accent-blue)",
};

export default function PaymentsPanel({ token, wsRequest, canEdit, modeStatus }) {
  const [filter, setFilter] = useState("all");
  // `query` is what is typed; `search` is what has been submitted. Kept
  // apart deliberately — searching on every keystroke would fire a
  // request per character against a list that can be long.
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  // LIVE OR TEST PAYMENTS (1.468.0). This list was locked to the admin's
  // own mode, so from Live the test site's payments were not merely
  // unlabelled - they were invisible, which is a problem the moment a
  // partner tests a checkout.
  const [view, setView] = useState("live");
  // ONLY ASK WHILE THE SITE IS LIVE. The filter is not drawn in Test or
  // Demo - there is nothing to choose - but the state behind it still
  // says "live", and asking for Live from Test is refused by the same
  // rule that makes the filter pointless there. Sending nothing means
  // the admin's own mode, which is the right answer in every mode.
  const askMode = (modeStatus?.effectiveMode || "live") === "live" ? view : undefined;
  const [busyId, setBusyId] = useState(null);
  const [confirming, setConfirming] = useState(null);
  // THE REFUND CONFIRMATION, the same shape as the dispute one above.
  // Was a window.prompt, which looks like 1998 and cannot be styled.
  const [refunding, setRefunding] = useState(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [note, setNote] = useState("");

  // NO PANEL-WIDE `frozen` OR `locked` ANY MORE.
  //
  // They meant "the admin is not in Live mode", and every control on a
  // row used them — which made the whole panel read-only in Test, on
  // payments that are only visible in Test. Each row now judges by its
  // OWN mode instead; see rowFrozen where the rows are built.
  //
  // Deleted rather than left unused, because the next person to add a
  // control here would reach for `locked` and get the old behaviour back
  // without noticing.

  // Root's sitewide list defaults — the STARTING point for how this list
  // is displayed. usePaginatedList remembers a viewer's own choice per
  // list from there.
  const [siteDefaults, setSiteDefaults] = useState(null);
  useEffect(() => {
    wsRequest("siteSettings.get")
      .then((d) => setSiteDefaults({ mode: d.listDisplayMode, pageSize: d.listPageSize }))
      .catch(() => {});
  }, []);

  // THE SHARED HOOK, not a bespoke one.
  //
  // Every other admin list uses this: it supports pagination or infinite
  // scroll, remembers which the viewer chose for THIS list, and honours
  // root's sitewide default until they choose. A hand-rolled Previous/Next
  // here would have been the only list on the site that ignored all of
  // that.
  //
  // `deps` resets to page one whenever the filters change — staying on
  // page 4 of a result set that now has two pages shows an empty list and
  // looks like a fault.
  const {
    mode,
    setMode,
    pageSize,
    setPageSize,
    page,
    setPage,
    items: rows,
    totalMatching: total,
    totalPages,
    loading,
    loadMore,
    hasMore,
    reload,
  } = usePaginatedList({
    listKey: "admin-payments",
    defaultMode: siteDefaults?.mode,
    defaultPageSize: siteDefaults?.pageSize,
    deps: [filter, search, from, to, modeStatus?.effectiveMode, askMode],
    fetchPage: ({ offset, limit }) =>
      wsRequest(
        "admin.payments.list",
        { status: filter, q: search, from, to, offset, limit, mode: askMode },
        token
      ),
  });

  const load = () => reload();

  // REFUNDING, BY HAND.
  //
  // Deliberately NOT offered on a payment marked disputed: refunding one
  // credits the cardholder twice and debits the merchant twice, which is
  // the whole reason the disputed mark exists. The server refuses it too
  // — this only saves somebody the round trip.
  //
  // The amount is optional and defaults to whatever is left, because the
  // provider allows several partial refunds up to the original value.
  // THE POLICY, READ ONCE.
  //
  // Finance → Refunds decides whether refunds are offered at all, and
  // what one means. The server enforces it; this is so the button is not
  // offered only to fail with a 409.
  //
  // Defaults to "none" while it loads, so the control appears once it is
  // known to be allowed rather than disappearing a moment later.
  const [refundPolicy, setRefundPolicy] = useState("none");
  useEffect(() => {
    wsRequest("admin.siteSettings.private", {}, token)
      .then((s) => setRefundPolicy(s?.refundPolicy || "none"))
      .catch(() => {});
  }, [token]);

  const remainingOn = (row) => (Number(row.price) || 0) - (Number(row.refundedTotal) || 0);

  const refund = async (row) => {
    const remaining = remainingOn(row);
    const amount = Number(refundAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      showToast("That is not an amount.", "error");
      return;
    }
    if (amount > remaining + 0.001) {
      showToast(`That is more than the ${remaining.toFixed(2)} left to refund.`, "error");
      return;
    }
    setBusyId(row.id);
    try {
      const res = await wsRequest("admin.purchase.refund", { pendingId: row.id, amount }, token);
      // THE REFERENCE IS WORTH SHOWING. It is what the buyer would quote,
      // and what ties this refund to the purchase at the provider.
      showToast(
        res.done
          ? `Refunded. Reference ${res.reference}.`
          : `Sent, but the provider reported "${res.status}" rather than completed — check before retrying. Reference ${res.reference}.`,
        res.done ? "success" : "error"
      );
      setRefunding(null);
      setRefundAmount("");
      load();
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setBusyId(null);
  };

  const setDisputed = async (row, disputed) => {
    setBusyId(row.id);
    try {
      await wsRequest(
        "admin.payments.setDisputed",
        { id: row.id, disputed, note: disputed ? note : "" },
        token
      );
      showToast(disputed ? "Marked as disputed." : "Dispute mark removed.");
      setConfirming(null);
      setNote("");
      load();
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    } finally {
      setBusyId(null);
    }
  };

  const money = (row) => {
    const amount =
      row.kind === "credit-topup" && row.topUpAmountMinor != null
        ? (row.topUpAmountMinor / 100).toFixed(2)
        : Number(row.price || 0).toFixed(2);
    return (row.currency ? row.currency + " " : "") + amount;
  };

  return (
    <section style={{ marginTop: 28 }}>
      <h3 style={{ marginBottom: 4 }}>Payments</h3>
      <p style={{ fontSize: 13, opacity: 0.8, marginTop: 0 }}>
        Everything that went to a payment provider — including the ones that did not complete.
      </p>
      <DataModeFilter
        value={view}
        onChange={setView}
        siteIsLive={(modeStatus?.effectiveMode || "live") === "live"}
        what="payments"
      />


      {/* ONE FREE-TEXT BOX. The realistic starting points — an email from
          a buyer, a job reference quoted in a dispute notification, a
          username from a support message — are all just a string somebody
          has in front of them. Making them say WHICH KIND of thing it is
          before they can look for it helps nobody. */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ flex: "1 1 260px" }}>
          <label style={{ display: "block", fontSize: 12, opacity: 0.75, marginBottom: 2 }}>
            Search
          </label>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") setSearch(query.trim());
            }}
            placeholder="Email, username, name, transaction or provider reference"
            style={{ width: "100%" }}
          />
        </div>
        <div>
          <label style={{ display: "block", fontSize: 12, opacity: 0.75, marginBottom: 2 }}>From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label style={{ display: "block", fontSize: 12, opacity: 0.75, marginBottom: 2 }}>To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button type="button" onClick={() => setSearch(query.trim())}>
          Search
        </button>
        {(search || from || to) && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setSearch("");
              setFrom("");
              setTo("");
            }}
          >
            Clear
          </button>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {["all", "confirmed", "failed", "pending"].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setFilter(s)}
            style={{
              padding: "4px 10px",
              borderRadius: 6,
              border:
                "1px solid " + (filter === s ? "var(--accent-blue)" : "var(--border-color)"),
              background: "transparent",
              color: "var(--text-primary)",
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}

      </div>

      <ListDisplayControls
        listKey="admin-payments"
        mode={mode}
        setMode={setMode}
        pageSize={pageSize}
        setPageSize={setPageSize}
        page={page}
        setPage={setPage}
        totalPages={totalPages}
        totalMatching={total}
        itemsCount={rows?.length || 0}
        loading={loading}
        loadMore={loadMore}
        hasMore={hasMore}
      />

      {/* `items` is null until the first load completes — the hook says so
          at its declaration, and every other panel returns early on it.
          This one read `rows.length` straight away and crashed the tab on
          mount. Guarded here rather than with an early return, because
          the search controls above should stay usable while loading. */}
      {rows === null && <p style={{ opacity: 0.7 }}>Loading…</p>}

      {rows !== null && rows.length === 0 && (
        <p style={{ opacity: 0.7 }}>
          {search || from || to
            ? "Nothing matches that search."
            : "No payments to show."}
        </p>
      )}

      {(rows || []).map((row) => {
        // FROZEN BY THE PAYMENT'S OWN MODE, not by "is this Live".
        //
        // The panel used to disable every control unless the admin was in
        // Live mode. But a payment is only VISIBLE in its own mode, so
        // that made the whole panel read-only in Test — including the
        // dispute mark, which is how you would set up a test of the
        // refund guard that reads it.
        //
        // Nothing here moves money on its own: marking a payment disputed
        // is bookkeeping, and the server never froze it at all. This was
        // only ever a UI rule.
        const rowFrozen = (row.mode || "live") !== (modeStatus?.effectiveMode || "live");
        const rowLocked = !canEdit || rowFrozen;
        const rowFrozenTitle = `This is ${row.mode || "live"} data — switch to ${
          row.mode || "live"
        } mode to manage it`;
        return (
        <div
          key={row.id}
          style={{
            border: "1px solid " + (row.disputed ? "var(--accent-red)" : "var(--border-color)"),
            borderRadius: 8,
            padding: "10px 12px",
            marginBottom: 8,
            fontSize: 13,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <span>
              <strong style={{ color: STATUS_COLOURS[row.status] || "var(--text-primary)" }}>
                {row.status}
              </strong>
              {" — "}
              <strong>{money(row)}</strong>
              {row.kind === "credit-topup" ? " entry credit top-up" : " pixels"}
              {/* WHAT HAS BEEN GIVEN BACK, on the row rather than behind a
                  click. A partly-refunded payment looks identical to a
                  whole one otherwise, which is how somebody refunds the
                  same thing twice. */}
              {Number(row.refundedTotal) > 0 && (
                <span style={{ opacity: 0.85, color: "var(--accent-blue)" }}>
                  {" "}
                  · {Number(row.refundedTotal).toFixed(2)} refunded
                  {Number(row.refundedTotal) >= (Number(row.price) || 0) ? " in full" : ""}
                  {row.refundReference ? ` (${row.refundReference})` : ""}
                </span>
              )}
              {row.recoveredFromFailed && (
                <span style={{ opacity: 0.75 }}> · recovered from failed</span>
              )}
            </span>
            <span style={{ opacity: 0.7 }}>{siteDateTime(row.createdAt)}</span>
          </div>

          <div style={{ opacity: 0.75, marginTop: 4 }}>
            {row.buyerUsername ? row.buyerUsername + " · " : ""}
            {row.buyerEmail || row.buyerId} · {row.provider || "no provider"} ·{" "}
            {row.providerMode || "?"} · {row.credentialSource || "?"} credentials
            {row.settledBy ? " · settled by " + row.settledBy : ""}
          </div>

          {/* BOTH REFERENCES, because a refund needs both and because
              these are what an admin quotes when emailing the provider
              about a dispute. */}
          <div style={{ opacity: 0.6, marginTop: 4, fontFamily: "monospace", fontSize: 11.5 }}>
            job {row.providerReference || "—"} · payment {row.paymentReference || "—"}
          </div>

          {row.disputed && (
            <div
              style={{
                marginTop: 8,
                padding: "8px 10px",
                border: "1px solid var(--accent-red)",
                borderRadius: 6,
              }}
            >
              <strong style={{ color: "var(--accent-red)" }}>Disputed — do not refund.</strong>{" "}
              Refunding a payment already in dispute credits the cardholder twice and debits you
              twice.
              <div style={{ opacity: 0.75, marginTop: 4 }}>
                Marked by {row.disputedBy}
                {row.disputedAt ? " on " + siteDateTime(row.disputedAt) : ""}
                {row.disputeNote ? " — " + row.disputeNote : ""}
              </div>
            </div>
          )}

          <Frozen frozen={rowFrozen} title={rowFrozenTitle}>
            <div style={{ marginTop: 8 }}>
              {row.disputed ? (
                <button
                  type="button"
                  disabled={rowLocked || busyId === row.id}
                  onClick={() => setDisputed(row, false)}
                >
                  {busyId === row.id ? "Working…" : "Remove dispute mark"}
                </button>
              ) : (
                <button
                  type="button"
                  disabled={rowLocked || busyId === row.id || row.status !== "confirmed"}
                  title={
                    row.status !== "confirmed"
                      ? "Only a payment that completed can be disputed"
                      : undefined
                  }
                  onClick={() => {
                    setConfirming(row.id);
                    setNote("");
                  }}
                >
                  Mark as disputed
                </button>
              )}
            </div>
          </Frozen>

          {/* OUTSIDE <Frozen>, AND THAT IS THE POINT.

              Frozen renders a <fieldset disabled> when the admin is not
              in Live mode, which disables EVERY control inside it
              regardless of the control's own `disabled` prop. The
              refund button has its own rule — a payment is refundable
              in its own mode — so it cannot live in there. */}

          {/* REFUND, beside the dispute control because they are the
              two things an operator does to a completed payment.

              Not offered on a disputed one: refunding it would credit
              the cardholder twice. The server refuses it too — this
              just saves the round trip and explains why. */}
          <button
            type="button"
            disabled={
              // NOT `locked`, which means "not Live". A refund is
              // judged by the PAYMENT's mode: a test payment can be
              // refunded from Test mode, where no real money moves
              // and the path can be proved before it matters. The
              // server does the same check.
              !canEdit ||
              (row.mode || "live") !== (modeStatus?.effectiveMode || "live") ||
              busyId === row.id ||
              row.status !== "confirmed" ||
              row.disputed ||
              refundPolicy !== "money" ||
              (Number(row.price) || 0) - (Number(row.refundedTotal) || 0) <= 0
            }
            title={
              (row.mode || "live") !== (modeStatus?.effectiveMode || "live")
                ? `This is ${row.mode || "live"} data — switch to ${row.mode || "live"} mode to refund it`
                : refundPolicy === "none"
                ? "Refunds are switched off under Finance → Refunds"
                : refundPolicy !== "money"
                  ? `The refund policy is "${refundPolicy}", which does more than return the money — that part is not built yet`
                  : row.disputed
                ? "A disputed payment must not be refunded — the cardholder would be credited twice"
                : row.status !== "confirmed"
                  ? "Only a payment that completed can be refunded"
                  : (Number(row.price) || 0) - (Number(row.refundedTotal) || 0) <= 0
                    ? "Already refunded in full"
                    : undefined
            }
            onClick={() => {
              setRefunding(row.id);
              setRefundAmount(remainingOn(row).toFixed(2));
            }}
          >
            {busyId === row.id ? "Working…" : "Refund"}
          </button>

          {refunding === row.id && (
            <div
              style={{
                marginTop: 8,
                padding: "10px 12px",
                border: "1px solid var(--border-color)",
                borderRadius: 6,
              }}
            >
              <p style={{ marginTop: 0 }}>
                Refund how much of {remainingOn(row).toFixed(2)}?{" "}
                {Number(row.refundedTotal) > 0 && (
                  <>({Number(row.refundedTotal).toFixed(2)} already refunded.) </>
                )}
                This sends money back to the card that paid. It does not take back anything
                that was bought.
              </p>
              <input
                type="number"
                step="0.01"
                min="0"
                max={remainingOn(row).toFixed(2)}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                style={{ width: "100%", marginBottom: 8 }}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => {
                    setRefunding(null);
                    setRefundAmount("");
                  }}
                  disabled={busyId === row.id}
                >
                  Cancel
                </button>
                <button type="button" onClick={() => refund(row)} disabled={busyId === row.id}>
                  {busyId === row.id ? "Refunding…" : "Refund"}
                </button>
              </div>
            </div>
          )}

          {confirming === row.id && (
            <div
              style={{
                marginTop: 8,
                padding: "10px 12px",
                border: "1px solid var(--border-color)",
                borderRadius: 6,
              }}
            >
              <p style={{ marginTop: 0 }}>
                Mark this payment as disputed? Do this when you receive a chargeback or dispute
                notification from your payment provider — nothing here can detect one for you.
              </p>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Reference or note (optional)"
                style={{ width: "100%", marginBottom: 8 }}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" onClick={() => setConfirming(null)} disabled={busyId === row.id}>
                  Cancel
                </button>
                <button type="button" onClick={() => setDisputed(row, true)} disabled={busyId === row.id}>
                  {busyId === row.id ? "Working…" : "Mark as disputed"}
                </button>
              </div>
            </div>
          )}
        </div>
        );
      })}

    </section>
  );
}
